/**
 * Code Health Dashboard — vue SuperAdmin sur la dette technique du projet.
 * LOC, complexité, fichiers les plus gros, red flags, coverage Pytest.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Activity, FileCode, Cpu, AlertTriangle, RefreshCw, GitCommit,
  Loader2, TrendingDown, ShieldCheck, FlaskConical, FileText, Zap,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getAuthHeader() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function StatCard({ icon: Icon, label, value, sub, color = 'cyan' }) {
  const palette = {
    cyan: 'from-cyan-500/20 to-blue-700/20 border-cyan-400/30 text-cyan-200',
    purple: 'from-purple-500/20 to-fuchsia-700/20 border-purple-400/30 text-purple-200',
    emerald: 'from-emerald-500/20 to-teal-700/20 border-emerald-400/30 text-emerald-200',
    orange: 'from-orange-500/20 to-red-700/20 border-orange-400/30 text-orange-200',
    yellow: 'from-yellow-500/20 to-amber-700/20 border-yellow-400/30 text-yellow-200',
  }[color] || 'from-slate-500/20 to-slate-700/20 border-slate-400/30 text-slate-200';
  return (
    <div className={`rounded-xl bg-gradient-to-br ${palette} backdrop-blur-sm border p-4`}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <p className="text-3xl font-bold mt-1 text-white">{value}</p>
      {sub && <p className="text-xs mt-1 opacity-80">{sub}</p>}
    </div>
  );
}

function FileTable({ title: title, items, columns, emptyText, testId }) {
  if (!items || items.length === 0) {
    return <p className="text-slate-400 text-sm italic">{emptyText || 'Aucune donnée'}</p>;
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white" data-testid={testId}>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-700">
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={`px-3 py-2 text-${c.align || 'left'} font-semibold text-xs uppercase tracking-wider`}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {items.map((it, i) => (
            <tr key={it.path + i} className="hover:bg-slate-50">
              {columns.map((c) => (
                <td key={c.key} className={`px-3 py-2 text-${c.align || 'left'}`}>
                  {c.render ? c.render(it[c.key], it) : it[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function CodeHealthDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (forceFresh = false) => {
    if (forceFresh) setRefreshing(true);
    else setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/code-health`, {
        headers: getAuthHeader(),
        params: { top_n: 15, no_cache: forceFresh },
      });
      setData(res.data);
      if (forceFresh) toast.success('Analyse rafraîchie');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(false); }, []);

  const handleInvalidate = async () => {
    try {
      await axios.post(`${API}/admin/code-health/cache/invalidate`, {}, { headers: getAuthHeader() });
      await load(true);
    } catch {
      toast.error('Erreur invalidation cache');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-500" data-testid="code-health-loading">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        Analyse du code en cours…
      </div>
    );
  }
  if (!data) return null;

  const { backend, frontend, tests, red_flags, git } = data;
  const totalLines = (backend?.lines_total || 0) + (frontend?.lines_total || 0);
  const totalFiles = (backend?.files_total || 0) + (frontend?.files_total || 0);

  return (
    <div className="space-y-6" data-testid="code-health-dashboard">
      {/* Hero Header — Ultra Pro */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-cyan-900 to-emerald-900 p-6 lg:p-8 text-white shadow-2xl">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(34,211,238,0.4),_transparent_55%)] pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,_rgba(16,185,129,0.25),_transparent_45%)] pointer-events-none" />
        <div className="relative">
          <div className="flex flex-col xl:flex-row items-start xl:items-center justify-between gap-5">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500 via-emerald-500 to-teal-400 flex items-center justify-center shadow-lg shadow-emerald-500/40 flex-shrink-0">
                <Activity className="h-7 w-7 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 border border-emerald-400/30 backdrop-blur-sm font-semibold text-xs flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    {data._cache_hit ? 'Données cachées (5 min)' : 'Analyse fraîche'}
                  </span>
                  {git?.sha && (
                    <span className="px-2 py-0.5 rounded-full bg-white/10 text-white border border-white/20 backdrop-blur-sm font-mono text-xs flex items-center gap-1.5">
                      <GitCommit className="h-3 w-3" /> {git.sha}
                    </span>
                  )}
                </div>
                <h2 className="text-2xl lg:text-3xl font-bold">Code Health Dashboard</h2>
                <p className="text-cyan-100 mt-1 text-sm lg:text-base">
                  Visualisation en temps réel de la dette technique : LOC, complexité, hot spots, coverage.
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleInvalidate} disabled={refreshing} className="bg-white/10 hover:bg-white/20 text-white border-white/20 backdrop-blur-sm" data-testid="btn-refresh-code-health">
                {refreshing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Rafraîchir l&apos;analyse
              </Button>
            </div>
          </div>

          {/* KPIs globaux */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-6">
            <StatCard icon={FileCode} label="Total fichiers" value={totalFiles.toLocaleString('fr-FR')} sub={`${backend?.files_total || 0} backend + ${frontend?.files_total || 0} frontend`} color="cyan" />
            <StatCard icon={FileText} label="Total LOC" value={totalLines.toLocaleString('fr-FR')} sub="lignes incl. blanks/comments" color="purple" />
            <StatCard icon={Cpu} label="Complexité moy." value={backend?.complexity_avg ?? '—'} sub="cyclomatic backend" color="emerald" />
            <StatCard icon={FlaskConical} label="Test files" value={tests?.test_files || 0} sub={tests?.coverage?.exists ? `Couverture ${tests.coverage.percent_covered}%` : 'Coverage absent'} color={tests?.coverage?.exists ? 'emerald' : 'yellow'} />
            <StatCard icon={AlertTriangle} label="Red flags" value={red_flags?.length || 0} sub="fichiers > 1500L ou CC > 30" color={red_flags?.length > 0 ? 'orange' : 'emerald'} />
          </div>
        </div>
      </div>

      {/* Red Flags */}
      {red_flags?.length > 0 && (
        <Card className="border-2 border-orange-200 shadow-md">
          <CardHeader className="bg-gradient-to-r from-orange-50 via-rose-50 to-red-50 border-b border-orange-100">
            <CardTitle className="flex items-center gap-2 text-slate-900">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              Red Flags ({red_flags.length})
            </CardTitle>
            <CardDescription>Fichiers &gt; 1500 lignes ou complexité maximale &gt; 30 — candidats au refactor.</CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-2">
              {red_flags.map((f, i) => (
                <div key={f.path + i} className="flex items-center justify-between p-3 rounded-lg bg-orange-50/50 border border-orange-200">
                  <div className="flex items-center gap-3">
                    <Badge className={
                      f.type === 'high_complexity'
                        ? 'bg-rose-700 text-white border-rose-800 font-semibold'
                        : 'bg-orange-700 text-white border-orange-800 font-semibold'
                    }>
                      {f.type === 'high_complexity' ? 'Complexité haute' : 'Fichier énorme'}
                    </Badge>
                    <code className="text-xs font-mono text-slate-700">{f.path}</code>
                    {f.function && <span className="text-xs text-slate-500">→ <code className="font-mono">{f.function}()</code></span>}
                  </div>
                  <span className="text-lg font-bold text-orange-700">{f.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Backend largest files */}
      <Card className="shadow-md">
        <CardHeader className="bg-gradient-to-r from-slate-50 via-blue-50 to-indigo-50 border-b border-slate-100">
          <CardTitle className="flex items-center gap-2 text-slate-900">
            <Zap className="h-5 w-5 text-blue-600" />
            Top 15 fichiers backend par taille
          </CardTitle>
          <CardDescription>Cibles prioritaires pour le découpage en modules.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <FileTable
            title="Backend"
            items={backend?.biggest_files || []}
            testId="biggest-backend-table"
            columns={[
              { key: 'path', label: 'Chemin', render: (v) => <code className="text-xs font-mono">{v}</code> },
              { key: 'lines_total', label: 'LOC', align: 'right', render: (v) => <span className={v > 2000 ? 'font-bold text-rose-600' : v > 1000 ? 'font-semibold text-orange-600' : ''}>{v.toLocaleString('fr-FR')}</span> },
              { key: 'lines_code', label: 'Code', align: 'right', render: (v) => <span className="text-slate-500">{v.toLocaleString('fr-FR')}</span> },
              { key: 'complexity_max', label: 'CC max', align: 'right', render: (v) => <span className={v > 30 ? 'font-bold text-rose-600' : v > 15 ? 'font-semibold text-orange-600' : 'text-slate-600'}>{v}</span> },
              { key: 'hottest_function', label: 'Fonction la + complexe', render: (v) => v ? <code className="text-xs font-mono text-slate-600">{v}()</code> : '—' },
            ]}
          />
        </CardContent>
      </Card>

      {/* Frontend largest files */}
      <Card className="shadow-md">
        <CardHeader className="bg-gradient-to-r from-slate-50 via-purple-50 to-fuchsia-50 border-b border-slate-100">
          <CardTitle className="flex items-center gap-2 text-slate-900">
            <FileCode className="h-5 w-5 text-purple-600" />
            Top 15 fichiers frontend par taille
          </CardTitle>
          <CardDescription>JS/JSX/TS/TSX — candidats au split en sous-composants.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <FileTable
            items={frontend?.biggest_files || []}
            testId="biggest-frontend-table"
            columns={[
              { key: 'path', label: 'Chemin', render: (v) => <code className="text-xs font-mono">{v}</code> },
              { key: 'lines_total', label: 'LOC', align: 'right', render: (v) => <span className={v > 1500 ? 'font-bold text-rose-600' : v > 800 ? 'font-semibold text-orange-600' : ''}>{v.toLocaleString('fr-FR')}</span> },
              { key: 'function_count', label: 'Functions', align: 'right' },
              { key: 'complexity_estimate', label: 'CC moy.', align: 'right' },
            ]}
          />
        </CardContent>
      </Card>

      {/* Most complex backend functions */}
      <Card className="shadow-md">
        <CardHeader className="bg-gradient-to-r from-slate-50 via-rose-50 to-pink-50 border-b border-slate-100">
          <CardTitle className="flex items-center gap-2 text-slate-900">
            <Cpu className="h-5 w-5 text-rose-600" />
            Top 15 fonctions backend par complexité cyclomatique
          </CardTitle>
          <CardDescription>Indicateur de difficulté à tester — viser CC ≤ 10 idéalement.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <FileTable
            items={backend?.most_complex_files || []}
            testId="most-complex-backend-table"
            columns={[
              { key: 'path', label: 'Chemin', render: (v) => <code className="text-xs font-mono">{v}</code> },
              { key: 'hottest_function', label: 'Fonction', render: (v) => v ? <code className="text-xs font-mono text-slate-700">{v}()</code> : '—' },
              { key: 'complexity_max', label: 'CC max', align: 'right', render: (v) => <span className={v > 30 ? 'font-bold text-rose-700' : v > 15 ? 'font-semibold text-orange-600' : 'text-slate-600'}>{v}</span> },
              { key: 'complexity_avg', label: 'CC moy.', align: 'right' },
              { key: 'lines_total', label: 'LOC', align: 'right' },
            ]}
          />
        </CardContent>
      </Card>

      {/* Coverage status */}
      <CoverageSection initialTests={tests} />
    </div>
  );
}

// ============================================================================
// Coverage Section — peut déclencher un run pytest+coverage en arrière-plan
// ============================================================================
function CoverageSection({ initialTests }) {
  const [coverage, setCoverage] = useState(null);
  const [running, setRunning] = useState(false);
  const [polling, setPolling] = useState(false);

  const fetchCoverage = async () => {
    try {
      const res = await axios.get(`${API}/admin/code-health/coverage`, { headers: getAuthHeader() });
      setCoverage(res.data);
      if (!res.data.running) setPolling(false);
      return res.data;
    } catch {
      // 404 = no report yet
      setCoverage({ exists: false });
      return null;
    }
  };

  useEffect(() => { fetchCoverage(); }, []);

  const handleRun = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${API}/admin/code-health/coverage/run`, {}, { headers: getAuthHeader() });
      if (res.data.status === 'already_running') {
        toast.info(res.data.message);
      } else {
        toast.success('Run pytest+coverage lancé. Résultats dans ~30-60s.');
      }
      setPolling(true);
    } catch {
      toast.error('Erreur au lancement');
    } finally {
      setRunning(false);
    }
  };

  // Polling toutes les 5s pendant que le run tourne
  useEffect(() => {
    if (!polling) return;
    const interval = setInterval(async () => {
      const data = await fetchCoverage();
      if (data && !data.running && data.exists) {
        clearInterval(interval);
        setPolling(false);
        toast.success(`Coverage : ${data.percent_covered?.toFixed(1)}%`);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [polling]);

  const exists = coverage?.exists;
  const isRunning = coverage?.running || polling;

  return (
    <Card className="shadow-md">
      <CardHeader className="bg-gradient-to-r from-slate-50 via-emerald-50 to-teal-50 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-slate-900">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
              Couverture Pytest
            </CardTitle>
            <CardDescription>
              {initialTests?.test_files || 0} fichier(s) de test détecté(s).
              {coverage?.generated_at && ` Dernier rapport : ${coverage.generated_at}`}
            </CardDescription>
          </div>
          <Button
            onClick={handleRun}
            disabled={running || isRunning}
            className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white"
            data-testid="btn-run-coverage"
          >
            {running || isRunning ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {isRunning ? 'Run en cours…' : 'Lancement…'}</>
            ) : (
              <><FlaskConical className="h-4 w-4 mr-2" /> {exists ? 'Relancer le run' : 'Lancer pytest+coverage'}</>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        {exists ? (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
              <StatCard icon={ShieldCheck} label="Coverage" value={`${coverage.percent_covered?.toFixed(1)}%`} color={coverage.percent_covered > 70 ? 'emerald' : coverage.percent_covered > 40 ? 'yellow' : 'orange'} />
              <StatCard icon={FileText} label="Statements" value={coverage.num_statements?.toLocaleString('fr-FR')} color="cyan" />
              <StatCard icon={ShieldCheck} label="Couvertes" value={coverage.covered_lines?.toLocaleString('fr-FR')} color="emerald" />
              <StatCard icon={TrendingDown} label="Manquantes" value={coverage.missing_lines?.toLocaleString('fr-FR')} color="orange" />
            </div>
            {coverage.worst_covered_files?.length > 0 && (
              <div>
                <h4 className="text-sm font-bold text-slate-900 mb-2">Top 15 fichiers les moins couverts</h4>
                <FileTable
                  items={coverage.worst_covered_files}
                  testId="worst-covered-table"
                  columns={[
                    { key: 'path', label: 'Chemin', render: (v) => <code className="text-xs font-mono">{v}</code> },
                    { key: 'percent_covered', label: 'Coverage', align: 'right', render: (v) => <span className={v < 30 ? 'font-bold text-rose-700' : v < 60 ? 'font-semibold text-orange-600' : 'text-emerald-700'}>{v?.toFixed(1)}%</span> },
                    { key: 'num_statements', label: 'Statements', align: 'right' },
                    { key: 'missing_lines', label: 'Manquantes', align: 'right', render: (v) => <span className="text-rose-600 font-semibold">{v}</span> },
                  ]}
                />
              </div>
            )}
          </>
        ) : (
          <div className="rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 p-6 text-center">
            <ShieldCheck className="h-8 w-8 text-slate-400 mx-auto mb-2" />
            <p className="text-sm text-slate-500">
              Aucun rapport de couverture pour l&apos;instant.
            </p>
            <p className="text-xs text-slate-400 mt-1">
              Cliquez sur <strong>« Lancer pytest+coverage »</strong> ci-dessus pour générer le premier rapport.
              Durée ≈ 30-60 secondes.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
